import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { BalanceEntry } from '../../api/client';
import { balance } from '../../api/client';
import { useToast } from '../../components/Toast';
import { Badge, Button, EmptyState, ErrorState, Modal, Provenance, Spinner } from '../../components/ui';
import { balanceMood, carriedInBalance } from '../../lib/balance-display';
import { formatAmount, formatDate } from '../../lib/format';
import { usePersistedFlag } from '../../lib/use-persisted-flag';
import { BalanceEntryModal } from './BalanceEntryModal';

/**
 * The balance card on the reimbursement report (`LLM_CONTEXT/11-balance.md`).
 *
 * **Period-scoped only, deliberately.** `from` / `to` are the report's own
 * selectors, and `GET /balance` filters both sides of `funded − spent` on
 * `occurredAt` — so an entry outside the window never shows up in the rows,
 * even though it does change the all-time figure. Rather than a second toggle
 * (and a second cache slice), the card prints the balance *carried into* the
 * period, which the API derives as the pre-window running balance of
 * `perMonth[]`. Period + carried-in adds up to all-time, without an all-time
 * read.
 */

/** Server pagination over the window's rows (the API caps `limit` at 200). */
const ENTRIES_PER_PAGE = 20;

/** The ledger starts folded; whether the user opened it is a per-user memory. */
const ENTRIES_OPEN_KEY = 'reports.balanceEntriesOpen';

export function BalanceCard({ from, to }: { from: string; to: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [entriesOpen, setEntriesOpen] = usePersistedFlag(ENTRIES_OPEN_KEY, false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<BalanceEntry | null>(null);
  const [deleting, setDeleting] = useState<BalanceEntry | null>(null);

  const query = useQuery({
    queryKey: ['balance', from, to, page],
    queryFn: ({ signal }) => balance.get({ from, to, page, limit: ENTRIES_PER_PAGE, perMonth: true }, signal),
  });

  // A new period starts on its first page: page 3 of a shorter window would
  // come back empty and look like a bug.
  useEffect(() => {
    setPage(1);
  }, [from, to]);

  const remove = useMutation({
    mutationFn: (id: string) => balance.removeEntry(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['balance'] });
      toast('Ledger entry deleted', 'success');
      setDeleting(null);
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const data = query.data;
  const meta = data?.entries.meta;
  const mood = data ? balanceMood(data.balance) : null;
  const carriedIn = carriedInBalance(data?.perMonth);
  const totalPages = meta?.totalPages ?? 1;

  return (
    <section className="card balance-card" aria-label="Balance" data-testid="balance-card">
      {query.isPending ? (
        <Spinner label="Counting the balance" />
      ) : query.isError ? (
        <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
      ) : data && mood ? (
        <>
          <header className="balance-card__head">
            <div>
              <h2 className="card__title">Balance</h2>
              <p className="balance-card__hint">
                Funded − spent for {formatDate(from)} — {formatDate(to)}
              </p>
            </div>
            <Button variant="primary" className="no-print" onClick={() => setFormOpen(true)}>
              Add funding
            </Button>
          </header>

          <div className="balance-card__grid">
            <div className="balance-card__main">
              <div className={`balance-card__num balance-card__num--${mood.modifier}`} data-testid="balance-value">
                {formatAmount(data.balance, 'RUB')}
              </div>
              {/* No mood chip here on purpose (owner request, 2026-09-08): the
                  signed number plus its colour carry the state — red when the
                  company owes the owner, green when money is still available.
                  `balance-display.ts` is the one place that convention lives. */}
              {/* The window's edges are visible, not hidden: this is the number
                  the period carries in, so it can be checked against all-time. */}
              <p className="balance-card__note">
                {carriedIn === null
                  ? 'Nothing happened in this period.'
                  : `Carried in from before the period: ${formatAmount(carriedIn, 'RUB')}`}
              </p>
            </div>
            <div className="balance-card__figures">
              <div className="balance-figure">
                <span className="balance-figure__label">Funded (₽)</span>
                <span className="balance-figure__value">{formatAmount(data.funded, 'RUB')}</span>
              </div>
              <div className="balance-figure">
                <span className="balance-figure__label">Spent (₽)</span>
                <span className="balance-figure__value">{formatAmount(data.spent, 'RUB')}</span>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="balance-card__toggle"
            aria-expanded={entriesOpen}
            onClick={() => setEntriesOpen((open) => !open)}
          >
            <span className="balance-card__chevron" aria-hidden="true">
              {entriesOpen ? '▾' : '▸'}
            </span>
            Ledger entries ({meta?.total ?? 0})
          </button>

          {/* The actions and the pagination are a screen concern only. */}
          <div className="balance-card__entries">
            {entriesOpen ? (
              data.entries.items.length === 0 ? (
                <EmptyState
                  title="No ledger entries in this period"
                  hint="Fundings and corrections land here; payments come from the report itself."
                />
              ) : (
                <>
                  <div className="table-wrap">
                    <table className="data">
                      <thead>
                        <tr>
                          <th scope="col">Date</th>
                          <th scope="col">Kind</th>
                          <th scope="col" className="num">Amount</th>
                          <th scope="col" className="num">₽ equivalent</th>
                          <th scope="col">Source</th>
                          <th scope="col">Note</th>
                          <th scope="col" aria-label="Actions" />
                        </tr>
                      </thead>
                      <tbody>
                        {data.entries.items.map((entry) => (
                          <BalanceEntryRow
                            key={entry.id}
                            entry={entry}
                            onEdit={(row) => {
                              setEditing(row);
                              setFormOpen(true);
                            }}
                            onDelete={setDeleting}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="pagination no-print">
                    <span>
                      Page {meta?.page ?? page} of {totalPages}
                      {meta ? ` · ${meta.total} total` : ''}
                    </span>
                    <div className="pagination__pages">
                      <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                        Previous
                      </Button>
                      <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              )
            ) : null}
          </div>
        </>
      ) : null}

      <BalanceEntryModal
        open={formOpen}
        entry={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
      />

      <Modal
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title="Delete ledger entry"
        description="The balance is recomputed on read, so nothing is left behind."
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={remove.isPending} onClick={() => deleting && remove.mutate(deleting.id)}>
              Delete
            </Button>
          </>
        }
      >
        <p>
          Delete the {deleting?.kind.toLowerCase() ?? 'entry'} of{' '}
          {deleting ? formatAmount(deleting.rubAmount, 'RUB') : ''}
          {deleting?.occurredAt ? ` from ${formatDate(deleting.occurredAt)}` : ''}?
        </p>
      </Modal>
    </section>
  );
}

function BalanceEntryRow({
  entry,
  onEdit,
  onDelete,
}: {
  entry: BalanceEntry;
  onEdit: (entry: BalanceEntry) => void;
  onDelete: (entry: BalanceEntry) => void;
}) {
  return (
    <tr data-balance-entry-id={entry.id}>
      <td className="cell-muted">{formatDate(entry.occurredAt)}</td>
      <td>
        {/* FUNDING is a category, not a direction — the sign is in the amount. */}
        <Badge tone={entry.kind === 'FUNDING' ? 'ok' : 'warn'}>{entry.kind}</Badge>
      </td>
      <td className="num">
        {formatAmount(entry.amount, entry.currency)}
        {entry.currency === 'RUB' || entry.fxRate === null ? null : (
          <span className="matrix__rub"> @ {entry.fxRate}</span>
        )}
      </td>
      <td className="num">
        {/* A RUB row books itself, so a second identical column is noise. */}
        {entry.currency === 'RUB' ? <span className="cell-muted">—</span> : formatAmount(entry.rubAmount, 'RUB')}
      </td>
      <td>
        <Badge tone={entry.source === 'MANUAL' ? 'neutral' : 'brand'} title={entry.createdBy ?? undefined}>
          {entry.source}
        </Badge>
      </td>
      <td className="balance-entry__note">
        {entry.note ? <span>{entry.note}</span> : <span className="cell-muted">—</span>}
        <Provenance createdBy={entry.createdBy} createdAt={entry.createdAt} updatedBy={entry.updatedBy} />
      </td>
      <td className="balance-entry__actions no-print">
        <Button size="sm" variant="secondary" onClick={() => onEdit(entry)}>
          Edit
        </Button>
        <Button size="sm" variant="danger" onClick={() => onDelete(entry)}>
          Delete
        </Button>
      </td>
    </tr>
  );
}
